import { batch } from 'react-redux';
import { getPaymentMethods } from 'Api/AccountApi';
import { getErrorMessageFromCode } from 'Api/EvergentApi';
import { setPaymentTypeProperty, setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';
import { getEntitlements } from 'State/User/UserActions';
import { resetShippingAddress, successfulPayment } from 'State/Checkout/CheckoutActions';
import { resetPromoCode } from 'State/PromoCode/PromoCodeActions';
import { hideOverlay } from 'State/Layouts/LayoutActions';
import {
  applyDefaultPromo,
  trackPaymentCompleted,
  trackPaymentError,
  selectPaymentMethodByType,
} from 'Utils/PaymentMethods';
import { logger } from 'Utils/Logger';
import { sendSheerVerificationId } from 'Utils/SheerIdHelpers';
import { processSegment } from 'Utils/Subscriptions';
import {
  CLEAR_PAYMENT_METHODS,
  GET_PAYMENT_METHOD_ERROR,
  GET_PAYMENT_METHOD_REQUEST,
  GET_PAYMENT_METHOD_SUCCESS,
} from '../Types';
import { clearSubscription } from 'State/Subscriptions/SubscriptionsActions';

export const getPaymentMethodRequest = () => ({ type: GET_PAYMENT_METHOD_REQUEST });
export const getPaymentMethodSuccess = (payload) => ({ type: GET_PAYMENT_METHOD_SUCCESS, payload });
export const getPaymentMethodError = (payload) => ({ type: GET_PAYMENT_METHOD_ERROR, payload });
export const clearPaymentMethods = () => ({ type: CLEAR_PAYMENT_METHODS });

export const fetchPaymentMethods = () => {
  return (dispatch) => {
    dispatch(getPaymentMethodRequest());
    return getPaymentMethods()
      .then((data) => dispatch(getPaymentMethodSuccess(data)))
      .catch((error) => {
        const errorMessage = error.message || 'Error: unable to get payment method.';
        dispatch(getPaymentMethodError(errorMessage));
      });
  };
};

export const payment = (paymentMethodType, paymentData, trackPaymentMethod, pageStartSourceData, pageFlowTypeData) => {
  return async (dispatch, getState) => {
    const { checkout, paymentMethods, plans, promoCode, purchaseMode, secondScreen, subscription, user } = getState();
    const isSkipDefaultPromo = !applyDefaultPromo({ subscription, promoCode, plans });
    // add transaction data here for prev apple pay session

    const payload = {
      promoCode,
      paymentMethods,
      purchaseMode,
      user,
      plans,
      checkout,
      subscription,
      isSkipDefaultPromo,
      secondScreen,
      ...paymentData,
    };
    const { selectedPlan } = plans;
    logger.started(`payment`, { trackPaymentMethod, isSkipDefaultPromo });
    let data = await selectPaymentMethodByType(paymentMethodType, payload);
    /* The below line exists because for now, it seems Apple Pay can return undefined here.
    Not sure why or in which circumstances yet.
    TODO: clean this up and not let ApplePay ever return undefined */
    data = data || {};
    const { isSuccess, orderID, errorCode, errorMessage } = data;
    console.debug(`payment processed: `, isSuccess, orderID, errorCode, errorMessage);
    if (isSuccess && orderID) {
      console.debug(`payment success: `, orderID);
      logger.completed(`payment`, { ...data, trackPaymentMethod });
      const { selectedPlan: { freePromo: { isFreeTrial = false } = {} } = {} } = plans;
      setSubscriptionStateProperty(isFreeTrial ? 'trialist' : 'new');
      setPaymentTypeProperty(paymentMethodType);
      dispatch(successfulPayment());
      processSegment(subscription, selectedPlan);
      await dispatch(getEntitlements());
      await sendSheerVerificationId();
      trackPaymentCompleted({
        trackPaymentMethod,
        selectedPlan,
        orderID,
        user,
        promoCode,
        pageStartSourceData,
        pageFlowTypeData,
      });

      batch(() => {
        dispatch(clearSubscription());
        dispatch(resetPromoCode());
        dispatch(resetShippingAddress());
        dispatch(hideOverlay());
      });
      return Promise.resolve(orderID);
    }
    // payment error
    logger.error(`payment`, { ...data, trackPaymentMethod }, new Error(errorMessage));

    trackPaymentError({ trackPaymentMethod, user, errorCode, errorMessage });
    dispatch(hideOverlay());
    const evergentErrorMessage = getErrorMessageFromCode(errorCode);
    return Promise.reject(Error(evergentErrorMessage || errorMessage));
  };
};
